// src/components/BetTracker.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../styles/BetTracker.module.css";
import { api, API_BASE } from "../api"; // axios instance with auth
import { useAuth } from "../components/AuthGate"; // ⭐ premium gating
import { parseOddsToDecimal } from "../utils/oddsParser";

const fmt = (n, d = 2) =>
  n == null || Number.isNaN(+n) ? "—" : (+n).toFixed(d);
const asNum = (v) => (Number.isFinite(+v) ? +v : 0);
const emptyLeg = () => ({
  fixture_id: "",
  teams: "",
  market: "",
  bookmaker: "",
  price: "",
});
const toKey = (value) => String(value ?? "").trim();
const toNumberish = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const isAccaBet = (bet = {}) => {
  if ((bet.bet_type || "").toLowerCase() === "acca") return true;
  if (bet.is_acca === true) return true;
  if (Array.isArray(bet.acca_legs) && bet.acca_legs.length > 0) return true;
  const bm = String(bet.bookmaker || "").toUpperCase();
  const mk = String(bet.market || "").toUpperCase();
  return bm === "ACCA" || mk.includes("ACCA") || mk.includes("PARLAY") || mk.includes("MULTI");
};
const sanitizeLeg = (leg = {}) => ({
  fixture_id: leg.fixture_id ? Number(leg.fixture_id) : null,
  teams: toKey(leg.teams) || null,
  market: toKey(leg.market) || null,
  bookmaker: toKey(leg.bookmaker) || null,
  price: parseOddsToDecimal(leg.price),
});
const isValidLeg = (leg = {}) => {
  const parsed = sanitizeLeg(leg);
  const hasTeamsOrFixture = Boolean(parsed.fixture_id) || Boolean(parsed.teams);
  return hasTeamsOrFixture && Boolean(parsed.market) && Number(parsed.price) > 1;
};
const combinedOdds = (legs = []) =>
  legs
    .map((leg) => parseOddsToDecimal(leg?.price))
    .filter((n) => Number.isFinite(n) && n > 1)
    .reduce((acc, n) => acc * n, 1);
const deriveAccaTeamsSummary = (legs = []) =>
  legs
    .map((leg) => toKey(leg?.teams))
    .filter(Boolean)
    .join(" / ");
const buildDisplayTeams = (bet = {}) => {
  if (toKey(bet.teams)) return toKey(bet.teams);
  if (!isAccaBet(bet)) return "—";
  const fromLegs = (Array.isArray(bet.acca_legs) ? bet.acca_legs : [])
    .map((leg) => toKey(leg?.teams))
    .filter(Boolean)
    .join(" / ");
  return fromLegs || "Acca";
};
const normalizeAccaLegs = (legs = []) =>
  (Array.isArray(legs) ? legs : []).map((leg) => ({
    fixture_id: leg?.fixture_id ? String(leg.fixture_id) : "",
    teams: toKey(leg?.teams),
    market: toKey(leg?.market),
    bookmaker: toKey(leg?.bookmaker),
    price: leg?.price ?? "",
  }));

export default function BetTracker() {
  const { isPremium } = useAuth(); // ⭐ know if user is premium
  const PREMIUM_VIEW_KEY = "bet_tracker_premium_view";

  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({
    bet_type: "single",
    fixture_id: "",
    teams: "",
    market: "",
    bookmaker: "",
    price: "",
    stake: "",
    acca_legs: [emptyLeg(), emptyLeg()],
    notes: "",
  });
  const [apiError, setApiError] = useState("");
  const [expandedAccas, setExpandedAccas] = useState({});
  const [editingBetId, setEditingBetId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [plRange, setPlRange] = useState("all");
  const [chartHoverIdx, setChartHoverIdx] = useState(null);
  const [premiumView, setPremiumView] = useState(() => {
    if (typeof window === "undefined") return "all";
    return window.localStorage.getItem(PREMIUM_VIEW_KEY) || "all";
  });

  // 🔗 prefill from URL once
  const location = useLocation();
  const navigate = useNavigate();
  const prefilledOnce = useRef(false);

  useEffect(() => {
    if (prefilledOnce.current) return;
    const sp = new URLSearchParams(location.search);
    const patch = {};
    [
      "bet_type",
      "fixture_id",
      "teams",
      "market",
      "bookmaker",
      "price",
      "stake",
      "acca_legs",
      "notes",
    ].forEach((k) => {
      const v = sp.get(k);
      if (v != null && v !== "") patch[k] = v;
    });
    if (Object.keys(patch).length) {
      if (patch.acca_legs) {
        try {
          const parsed = JSON.parse(patch.acca_legs);
          patch.acca_legs = normalizeAccaLegs(parsed);
          patch.bet_type = "acca";
        } catch {
          patch.acca_legs = [emptyLeg(), emptyLeg()];
        }
      }
      setForm((prev) => ({ ...prev, ...patch }));
      prefilledOnce.current = true;
      navigate("/bets", { replace: true });
    }
  }, [location.search, navigate]);

  const loadBets = async () => {
    setLoading(true);
    setApiError("");
    try {
      const { data } = await api.get("/api/user-bets"); // per-user bets
      setBets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading bets:", err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setApiError("Please log in to use your bet tracker.");
      } else {
        setApiError(err.message || "Failed to load bets");
      }
      setBets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBets();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PREMIUM_VIEW_KEY, premiumView);
  }, [premiumView]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (name === "bet_type") {
        if (value === "acca") {
          return {
            ...prev,
            bet_type: "acca",
            fixture_id: "",
            price: prev.price || "",
            teams: prev.teams || "",
            acca_legs:
              Array.isArray(prev.acca_legs) && prev.acca_legs.length >= 2
                ? prev.acca_legs
                : [emptyLeg(), emptyLeg()],
          };
        }
        return { ...prev, bet_type: "single" };
      }
      return { ...prev, [name]: value };
    });
  };

  const updateAccaLeg = (index, key, value) => {
    setForm((prev) => {
      const next = [...(prev.acca_legs || [])];
      next[index] = { ...(next[index] || emptyLeg()), [key]: value };
      return { ...prev, acca_legs: next };
    });
  };

  const addAccaLeg = () => {
    setForm((prev) => ({ ...prev, acca_legs: [...(prev.acca_legs || []), emptyLeg()] }));
  };

  const removeAccaLeg = (index) => {
    setForm((prev) => {
      const current = [...(prev.acca_legs || [])];
      if (current.length <= 2) return prev;
      current.splice(index, 1);
      return { ...prev, acca_legs: current };
    });
  };

  const addBet = async (e) => {
    e.preventDefault();
    setSaving(true);
    setApiError("");
    try {
      const isAccaForm = form.bet_type === "acca";
      const stake = Number(form.stake);
      if (!Number.isFinite(stake) || stake <= 0) {
        throw new Error("Stake must be greater than 0");
      }

      let body;
      if (isAccaForm) {
        const legs = (form.acca_legs || []).map(sanitizeLeg).filter((leg) => leg.price !== null);
        const validLegs = legs.filter(isValidLeg);
        if (validLegs.length < 2) {
          throw new Error("Acca requires at least 2 valid legs");
        }
        const price = combinedOdds(validLegs);
        body = {
          fixture_id: null,
          teams: toKey(form.teams) || deriveAccaTeamsSummary(validLegs) || null,
          bet_type: "acca",
          market: toKey(form.market) || `${validLegs.length} Leg Acca`,
          bookmaker: toKey(form.bookmaker) || null,
          price,
          stake,
          acca_legs: validLegs,
          notes: toKey(form.notes) || null,
        };
      } else {
        const price = parseOddsToDecimal(form.price);
        if (!Number.isFinite(price) || price <= 1) {
          throw new Error("Odds must be greater than 1");
        }
        const fixtureId = form.fixture_id ? Number(form.fixture_id) : null;
        const teams = toKey(form.teams);
        if (!fixtureId && !teams) {
          throw new Error("Add teams or select a fixture ID");
        }
        body = {
          fixture_id: fixtureId,
          teams: teams || null,
          bet_type: "single",
          market: toKey(form.market),
          bookmaker: toKey(form.bookmaker) || null,
          price,
          stake,
          notes: toKey(form.notes) || null,
        };
      }

      await api.post("/api/user-bets", body);
      setForm({
        bet_type: "single",
        fixture_id: "",
        teams: "",
        market: "",
        bookmaker: "",
        price: "",
        stake: "",
        acca_legs: [emptyLeg(), emptyLeg()],
        notes: "",
      });
      loadBets();
    } catch (err) {
      console.error("Error adding bet:", err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setApiError("Please log in to add bets to your tracker.");
      } else {
        setApiError(err.message || "Failed to add bet");
      }
    } finally {
      setSaving(false);
    }
  };

  const settleBet = async (id, result) => {
    setApiError("");
    try {
      await api.patch(`/api/user-bets/${id}`, { result }); // "won" | "lost" | "void"
      loadBets();
    } catch (err) {
      console.error("Error settling bet:", err);
      setApiError(err.message || "Failed to settle bet");
    }
  };

  const deleteBet = async (id) => {
    if (!window.confirm("Delete this bet?")) return;
    setApiError("");
    try {
      await api.delete(`/api/user-bets/${id}`);
      loadBets();
    } catch (err) {
      console.error("Error deleting bet:", err);
      setApiError(err.message || "Failed to delete bet");
    }
  };

  const cleanup = async (action, confirmText) => {
    if (!window.confirm(confirmText)) return;
    setApiError("");
    try {
      await api.post("/api/user-bets/cleanup", { action }); // "delete_zero_pending" | "delete_pending" | "delete_all"
    } catch (err) {
      console.error("Cleanup failed:", err);
      setApiError(err.message || "Cleanup failed");
    } finally {
      loadBets();
    }
  };

  const statusFiltered = useMemo(() => {
    if (filter === "all") return bets;
    if (filter === "pending") return bets.filter((b) => !b.result);
    return bets.filter(
      (b) => (b.result || "").toLowerCase() === filter.toLowerCase()
    );
  }, [bets, filter]);

  const premiumFiltered = useMemo(() => {
    if (!isPremium) return statusFiltered;
    if (premiumView === "all") return statusFiltered;

    if (premiumView === "high_odds_accas") {
      return statusFiltered.filter((b) => isAccaBet(b) && asNum(b.price) >= 3);
    }
    if (premiumView === "singles_low_odds") {
      return statusFiltered.filter((b) => !isAccaBet(b) && asNum(b.price) > 1 && asNum(b.price) < 2);
    }
    if (premiumView === "recent_30d") {
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return statusFiltered.filter((b) => {
        const dt = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dt >= cutoff;
      });
    }
    return statusFiltered;
  }, [isPremium, premiumView, statusFiltered]);

  const accaLegs = form.acca_legs || [];
  const validAccaLegs = accaLegs.filter(isValidLeg);
  const accaCombinedPrice = validAccaLegs.length ? combinedOdds(validAccaLegs) : null;

  // ---------- summary stats ----------
  const stats = useMemo(() => {
    const totalBets = bets.length;

    const staked = bets.reduce((acc, b) => acc + asNum(b.stake), 0);

    const returned = bets.reduce((acc, b) => {
      const stake = asNum(b.stake);
      const price = asNum(b.price);
      const res = (b.result || "").toUpperCase();
      if (res === "WON") return acc + stake * price;
      if (res === "VOID") return acc + stake;
      return acc;
    }, 0);

    const pnl = returned - staked;
    const roi = staked ? (pnl / staked) * 100 : 0;

    const wonBets = bets.filter(
      (b) => (b.result || "").toUpperCase() === "WON"
    );
    const lostBets = bets.filter(
      (b) => (b.result || "").toUpperCase() === "LOST"
    );
    const voidedBets = bets.filter(
      (b) => (b.result || "").toUpperCase() === "VOID"
    );
    const pendingBets = bets.filter((b) => !b.result);

    const settledCount = wonBets.length + lostBets.length;
    const hitRate = settledCount ? (wonBets.length / settledCount) * 100 : 0;

    const avgOdds =
      totalBets > 0
        ? bets.reduce((acc, b) => acc + asNum(b.price), 0) / totalBets
        : 0;

    const avgStake = totalBets ? staked / totalBets : 0;

    return {
      totalBets,
      staked,
      returned,
      pnl,
      roi,
      won: wonBets.length,
      lost: lostBets.length,
      voided: voidedBets.length,
      pending: pendingBets.length,
      hitRate,
      avgOdds,
      avgStake,
    };
  }, [bets]);

  // ---------- performance strip (last 15 settled bets) ----------
  const performance = useMemo(() => {
    const settled = bets.filter((b) =>
      ["WON", "LOST", "VOID"].includes((b.result || "").toUpperCase())
    );

    if (!settled.length) {
      return { recent: [] };
    }

    const sorted = [...settled].sort((a, b) => {
      // Prefer created_at if we have it, else fall back to id
      const da = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
      return da - db;
    });

    const recent = sorted.slice(-15); // last 15
    return { recent };
  }, [bets]);

  // ---------- P/L chart data (cumulative over time, premium-only) ----------
  const plHistory = useMemo(() => {
    const settled = bets.filter((b) =>
      ["WON", "LOST", "VOID"].includes((b.result || "").toUpperCase())
    );

    const now = Date.now();
    const ranged = settled.filter((b) => {
      if (plRange === "all") return true;
      const ts = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (!ts) return plRange === "all";
      const days = plRange === "7d" ? 7 : plRange === "30d" ? 30 : plRange === "90d" ? 90 : null;
      if (!days) return true;
      return ts >= now - days * 24 * 60 * 60 * 1000;
    });

    if (!ranged.length) {
      return { values: [], path: "", lastValue: 0, min: 0, max: 0, zeroY: null };
    }

    const sorted = [...ranged].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
      return da - db;
    });

    let running = 0;
    const values = [];
    sorted.forEach((b) => {
      const stake = asNum(b.stake);
      const price = asNum(b.price);
      const res = (b.result || "").toUpperCase();
      let delta = 0;

      if (res === "WON") delta = stake * (price - 1); // net win
      else if (res === "LOST") delta = -stake;        // stake lost
      else if (res === "VOID") delta = 0;

      running += delta;
      values.push(running);
    });

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const span = maxVal - minVal || 1;

    const h = 40;
    const w = 100;
    const pad = 4;

    const coords = values.map((v, idx) => {
      const x =
        values.length === 1
          ? w / 2
          : (idx / (values.length - 1)) * (w - 2 * pad) + pad;
      const y =
        h - pad - ((v - minVal) / span) * (h - 2 * pad); // invert Y
      return { x, y };
    });

    const path = coords
      .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
      .join(" ");

    // y-position for zero line (if 0 within or near range)
    let zeroY = h - pad - ((0 - minVal) / span) * (h - 2 * pad);
    zeroY = Math.max(pad, Math.min(h - pad, zeroY));

    return {
      values,
      path,
      points: coords,
      labels: sorted.map((b) => (b.created_at ? new Date(b.created_at).toLocaleDateString("en-GB") : `#${b.id}`)),
      lastValue: values[values.length - 1],
      min: minVal,
      max: maxVal,
      zeroY,
    };
  }, [bets, plRange]);

  const premiumInsights = useMemo(() => {
    const settled = bets
      .filter((b) => ["WON", "LOST", "VOID"].includes((b.result || "").toUpperCase()))
      .sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
        return da - db;
      });

    if (!settled.length) {
      return {
        longestWinStreak: 0,
        maxDrawdown: 0,
        bestMarket: "—",
        bestBookmaker: "—",
        marketBreakdown: [],
        bookmakerBreakdown: [],
        oddsBandBreakdown: [],
      };
    }

    let running = 0;
    let peak = 0;
    let maxDrawdown = 0;
    let currentWinStreak = 0;
    let longestWinStreak = 0;
    const byMarket = {};
    const byBookmaker = {};
    const byOddsBand = { "1.01-1.99": { pnl: 0, settled: 0 }, "2.00-2.99": { pnl: 0, settled: 0 }, "3.00+": { pnl: 0, settled: 0 } };

    settled.forEach((b) => {
      const stake = asNum(b.stake);
      const price = asNum(b.price);
      const res = (b.result || "").toUpperCase();
      let delta = 0;
      if (res === "WON") {
        delta = stake * (price - 1);
        currentWinStreak += 1;
        longestWinStreak = Math.max(longestWinStreak, currentWinStreak);
      } else if (res === "LOST") {
        delta = -stake;
        currentWinStreak = 0;
      } else {
        currentWinStreak = 0;
      }

      running += delta;
      if (running > peak) peak = running;
      const dd = peak - running;
      if (dd > maxDrawdown) maxDrawdown = dd;

      const marketKey = toKey(b.market) || "Unknown";
      const bookmakerKey = toKey(b.bookmaker) || "Unknown";
      byMarket[marketKey] = byMarket[marketKey] || { pnl: 0, settled: 0 };
      byBookmaker[bookmakerKey] = byBookmaker[bookmakerKey] || { pnl: 0, settled: 0 };
      byMarket[marketKey].pnl += delta;
      byMarket[marketKey].settled += 1;
      byBookmaker[bookmakerKey].pnl += delta;
      byBookmaker[bookmakerKey].settled += 1;

      const band = price >= 3 ? "3.00+" : price >= 2 ? "2.00-2.99" : "1.01-1.99";
      byOddsBand[band].pnl += delta;
      byOddsBand[band].settled += 1;
    });

    const topByPnl = (obj) =>
      Object.entries(obj)
        .map(([name, v]) => ({ name, pnl: v.pnl, settled: v.settled }))
        .sort((a, b) => b.pnl - a.pnl);

    return {
      longestWinStreak,
      maxDrawdown,
      bestMarket: topByPnl(byMarket)[0]?.name || "—",
      bestBookmaker: topByPnl(byBookmaker)[0]?.name || "—",
      marketBreakdown: topByPnl(byMarket).slice(0, 3),
      bookmakerBreakdown: topByPnl(byBookmaker).slice(0, 3),
      oddsBandBreakdown: topByPnl(byOddsBand),
    };
  }, [bets]);

  const exportCSV = () => {
    const header = [
      "id",
      "teams",
      "market",
      "bookmaker",
      "price",
      "stake",
      "result",
      "notes",
    ];
    const rows = premiumFiltered.map((b) => {
      return [
        b.id,
        buildDisplayTeams(b),
        b.market || "",
        b.bookmaker || "",
        asNum(b.price),
        asNum(b.stake),
        b.result || "",
        (b.notes || "").replace(/[\r\n]+/g, " "),
      ];
    });
    const csv = [header, ...rows]
      .map((r) =>
        r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bets.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleAccaRow = (betId) => {
    setExpandedAccas((prev) => ({ ...prev, [betId]: !prev[betId] }));
  };

  const startEditBet = (bet) => {
    const acca = isAccaBet(bet);
    setEditingBetId(bet.id);
    setEditDraft({
      teams: toKey(bet.teams),
      market: toKey(bet.market),
      bookmaker: toKey(bet.bookmaker),
      price: bet.price ?? "",
      stake: bet.stake ?? "",
      result: toKey(bet.result),
      bet_type: acca ? "acca" : "single",
      acca_legs: acca ? normalizeAccaLegs(bet.acca_legs) : [],
    });
  };

  const cancelEditBet = () => {
    setEditingBetId(null);
    setEditDraft(null);
  };

  const updateEditDraft = (key, value) => {
    setEditDraft((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const updateEditLeg = (index, key, value) => {
    setEditDraft((prev) => {
      const next = [...(prev?.acca_legs || [])];
      next[index] = { ...(next[index] || emptyLeg()), [key]: value };
      return { ...prev, acca_legs: next };
    });
  };

  const addEditLeg = () => {
    setEditDraft((prev) => ({ ...prev, acca_legs: [...(prev?.acca_legs || []), emptyLeg()] }));
  };

  const removeEditLeg = (index) => {
    setEditDraft((prev) => {
      const legs = [...(prev?.acca_legs || [])];
      if (legs.length <= 2) return prev;
      legs.splice(index, 1);
      return { ...prev, acca_legs: legs };
    });
  };

  const saveEditBet = async (bet) => {
    if (!editDraft || editingBetId !== bet.id) return;
    const patch = {};
    const originalIsAcca = isAccaBet(bet);
    const nextIsAcca = editDraft.bet_type === "acca";

    if (toKey(editDraft.teams) !== toKey(bet.teams)) patch.teams = toKey(editDraft.teams) || null;
    if (toKey(editDraft.market) !== toKey(bet.market)) patch.market = toKey(editDraft.market) || null;
    if (toKey(editDraft.bookmaker) !== toKey(bet.bookmaker)) patch.bookmaker = toKey(editDraft.bookmaker) || null;

    const nextStake = toNumberish(editDraft.stake);
    if (nextStake !== null && nextStake !== asNum(bet.stake)) patch.stake = nextStake;

    if (toKey(editDraft.result).toUpperCase() !== toKey(bet.result).toUpperCase()) {
      patch.result = toKey(editDraft.result) || null;
    }

    if (nextIsAcca) {
      const legs = (editDraft.acca_legs || []).map(sanitizeLeg).filter((leg) => leg.price !== null);
      const validLegs = legs.filter(isValidLeg);
      if (validLegs.length < 2) {
        setApiError("Acca update requires at least 2 valid legs");
        return;
      }
      const nextPrice = combinedOdds(validLegs);
      const prevLegs = JSON.stringify((Array.isArray(bet.acca_legs) ? bet.acca_legs : []).map(sanitizeLeg));
      const newLegs = JSON.stringify(validLegs.map(sanitizeLeg));
      if (prevLegs !== newLegs) patch.acca_legs = validLegs;
      if (asNum(bet.price) !== nextPrice) patch.price = nextPrice;
      if (!originalIsAcca) patch.bet_type = "acca";
      if (!toKey(editDraft.market)) patch.market = `${validLegs.length} Leg Acca`;
      if (!toKey(editDraft.teams)) patch.teams = deriveAccaTeamsSummary(validLegs) || null;
    } else {
      const nextPrice = parseOddsToDecimal(editDraft.price);
      if (nextPrice !== null && nextPrice > 1 && nextPrice !== asNum(bet.price)) patch.price = nextPrice;
      if (originalIsAcca) patch.bet_type = "single";
    }

    if (!Object.keys(patch).length) {
      cancelEditBet();
      return;
    }

    setApiError("");
    try {
      await api.patch(`/api/user-bets/${bet.id}`, patch);
      cancelEditBet();
      loadBets();
    } catch (err) {
      console.error("Error editing bet:", err);
      setApiError(err?.message || "Failed to update bet");
    }
  };

  const isRemote =
    !API_BASE.includes("127.0.0.1") && !API_BASE.includes("localhost");

  return (
    <div className={styles.wrap}>
      <h2 className={styles.title}>📊 Bet Tracker</h2>

      {/* Info banner */}
      <div className={styles.banner}>
        <b>Per-account tracker:</b>{" "}
        {isRemote
          ? "Log in to start tracking your own bets – they’re tied to your account."
          : "In local dev, make sure Firebase auth is configured so per-user bets work."}
      </div>

      {/* KPI Summary – premium vs free */}
      {isPremium ? (
        // ⭐ PREMIUM: full card-based analytics
        <div className={styles.kpis}>
          <div className={styles.kpiCard || ""}>
            <div className={styles.kpiLabel || ""}>Total Bets</div>
            <div className={styles.kpiValue || ""}>{stats.totalBets}</div>
          </div>
          <div className={styles.kpiCard || ""}>
            <div className={styles.kpiLabel || ""}>Staked</div>
            <div className={styles.kpiValue || ""}>£{fmt(stats.staked)}</div>
          </div>
          <div className={styles.kpiCard || ""}>
            <div className={styles.kpiLabel || ""}>Returned</div>
            <div className={styles.kpiValue || ""}>£{fmt(stats.returned)}</div>
          </div>
          <div
            className={`${styles.kpiCard || ""} ${
              stats.pnl >= 0
                ? styles.kpiPositive || ""
                : styles.kpiNegative || ""
            }`}
          >
            <div className={styles.kpiLabel || ""}>P/L</div>
            <div className={styles.kpiValue || ""}>£{fmt(stats.pnl)}</div>
          </div>
          <div className={styles.kpiCard || ""}>
            <div className={styles.kpiLabel || ""}>ROI</div>
            <div className={styles.kpiValue || ""}>{fmt(stats.roi)}%</div>
          </div>
          <div className={styles.kpiCard || ""}>
            <div className={styles.kpiLabel || ""}>Record</div>
            <div className={styles.kpiValue || ""}>
              {stats.won}W / {stats.lost}L / {stats.voided}V / {stats.pending}P
            </div>
          </div>
          <div className={styles.kpiCard || ""}>
            <div className={styles.kpiLabel || ""}>Hit Rate (settled)</div>
            <div className={styles.kpiValue || ""}>
              {fmt(stats.hitRate)}%
            </div>
          </div>
          <div className={styles.kpiCard || ""}>
            <div className={styles.kpiLabel || ""}>Avg Odds</div>
            <div className={styles.kpiValue || ""}>{fmt(stats.avgOdds)}</div>
          </div>
          <div className={styles.kpiCard || ""}>
            <div className={styles.kpiLabel || ""}>Avg Stake</div>
            <div className={styles.kpiValue || ""}>
              £{fmt(stats.avgStake)}
            </div>
          </div>
        </div>
      ) : (
        // 🆓 FREE: simple text row (feels more basic)
        <div className={styles.kpis}>
          <div>
            <b>Staked:</b> £{fmt(stats.staked)}
          </div>
          <div>
            <b>Returned:</b> £{fmt(stats.returned)}
          </div>
          <div>
            <b>P/L:</b> £{fmt(stats.pnl)}
          </div>
          <div>
            <b>ROI:</b> {fmt(stats.roi)}%
          </div>
          <div>
            <b>Record:</b> {stats.won}W / {stats.lost}L / {stats.voided}V /{" "}
            {stats.pending}P
          </div>
        </div>
      )}

      {isPremium && (
        <div className={styles.premiumTools}>
          <div className={styles.premiumViews}>
            <span>Views:</span>
            {[
              ["all", "All bets"],
              ["high_odds_accas", "High-odds accas"],
              ["singles_low_odds", "Singles <2.0"],
              ["recent_30d", "Last 30d"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={premiumView === key ? styles.premiumViewActive : ""}
                onClick={() => setPremiumView(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className={styles.premiumInsights}>
            <div className={styles.premiumInsightCard}>
              <span>Best Market</span>
              <b>{premiumInsights.bestMarket}</b>
            </div>
            <div className={styles.premiumInsightCard}>
              <span>Best Bookmaker</span>
              <b>{premiumInsights.bestBookmaker}</b>
            </div>
            <div className={styles.premiumInsightCard}>
              <span>Longest Win Streak</span>
              <b>{premiumInsights.longestWinStreak}</b>
            </div>
            <div className={styles.premiumInsightCard}>
              <span>Max Drawdown</span>
              <b>£{fmt(premiumInsights.maxDrawdown)}</b>
            </div>
          </div>
        </div>
      )}

      {/* Performance strip – ⭐ PREMIUM ONLY */}
      {isPremium && performance.recent.length > 0 && (
        <div className={styles.performance || ""} style={{ marginBottom: 10 }}>
          <div
            className={styles.performanceHeader || ""}
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "0.82rem",
              marginBottom: 4,
              opacity: 0.9,
            }}
          >
            <span>Recent form</span>
            <span className={styles.performanceMeta || ""}>
              Last {performance.recent.length} settled bets
            </span>
          </div>
          <div
            className={styles.performanceDots || ""}
            style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
          >
            {performance.recent.map((b) => {
              const res = (b.result || "").toUpperCase();
              let cls = styles.dotPending || "";
              if (res === "WON") cls = styles.dotWon || "";
              else if (res === "LOST") cls = styles.dotLost || "";
              else if (res === "VOID") cls = styles.dotVoid || "";

              return (
                <span
                  key={b.id}
                  className={cls}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background:
                      res === "WON"
                        ? "#34a853"
                        : res === "LOST"
                        ? "#d23b3b"
                        : res === "VOID"
                        ? "#f5e6b8"
                        : "rgba(234,244,237,0.6)",
                  }}
                  title={`${b.teams || b.market || "Bet"} – ${
                    res || "PENDING"
                  }`}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ⭐ PREMIUM ONLY – Cumulative P/L chart */}
      {isPremium && plHistory.values.length > 1 && (
        <div
          className={styles.plBlock || ""}
          style={{
            marginBottom: 14,
            marginTop: 2,
          }}
        >
          <div
            className={styles.plHeader || ""}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "0.82rem",
              marginBottom: 4,
              opacity: 0.9,
            }}
          >
            <span>P/L over time</span>
            <span className={styles.plMeta || ""}>
              Net:&nbsp;<b>£{fmt(plHistory.lastValue)}</b>
            </span>
          </div>
          <div className={styles.rangeToggles}>
            {[
              ["7d", "7D"],
              ["30d", "30D"],
              ["90d", "90D"],
              ["all", "All"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={plRange === key ? styles.rangeActive : ""}
                onClick={() => {
                  setPlRange(key);
                  setChartHoverIdx(null);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <svg
            viewBox="0 0 100 40"
            preserveAspectRatio="none"
            style={{
              width: "100%",
              height: 60,
              background: "rgba(0,0,0,0.55)",
              borderRadius: 8,
            }}
            onMouseMove={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              const x = ((event.clientX - rect.left) / rect.width) * 100;
              const points = plHistory.points || [];
              if (!points.length) return;
              let bestIdx = 0;
              let bestDist = Infinity;
              points.forEach((p, idx) => {
                const d = Math.abs(p.x - x);
                if (d < bestDist) {
                  bestDist = d;
                  bestIdx = idx;
                }
              });
              setChartHoverIdx(bestIdx);
            }}
            onMouseLeave={() => setChartHoverIdx(null)}
          >
            {/* zero axis */}
            {plHistory.zeroY != null && (
              <line
                x1="0"
                x2="100"
                y1={plHistory.zeroY}
                y2={plHistory.zeroY}
                stroke="rgba(255,255,255,0.18)"
                strokeWidth="0.4"
              />
            )}
            {/* P/L line */}
            <path
              d={plHistory.path}
              fill="none"
              stroke={
                plHistory.lastValue >= 0 ? "#34a853" : "#d23b3b"
              }
              strokeWidth="1.2"
            />
            {typeof chartHoverIdx === "number" && plHistory.points?.[chartHoverIdx] && (
              <circle
                cx={plHistory.points[chartHoverIdx].x}
                cy={plHistory.points[chartHoverIdx].y}
                r="1.6"
                fill="#fbbc04"
                stroke="#111"
                strokeWidth="0.6"
              />
            )}
          </svg>
          {typeof chartHoverIdx === "number" && (
            <div className={styles.chartHoverMeta}>
              {plHistory.labels?.[chartHoverIdx] || "—"} • £{fmt(plHistory.values?.[chartHoverIdx])}
            </div>
          )}
        </div>
      )}

      {isPremium && (
        <div className={styles.breakdowns}>
          <div className={styles.breakdownCard}>
            <div className={styles.breakdownTitle}>ROI by Market (Top 3)</div>
            {premiumInsights.marketBreakdown.length ? premiumInsights.marketBreakdown.map((row) => (
              <div key={`m-${row.name}`} className={styles.breakdownRow}>
                <span>{row.name}</span>
                <b className={row.pnl >= 0 ? styles.good : styles.bad}>£{fmt(row.pnl)}</b>
              </div>
            )) : <div className={styles.breakdownEmpty}>No settled bets yet.</div>}
          </div>
          <div className={styles.breakdownCard}>
            <div className={styles.breakdownTitle}>ROI by Bookmaker (Top 3)</div>
            {premiumInsights.bookmakerBreakdown.length ? premiumInsights.bookmakerBreakdown.map((row) => (
              <div key={`b-${row.name}`} className={styles.breakdownRow}>
                <span>{row.name}</span>
                <b className={row.pnl >= 0 ? styles.good : styles.bad}>£{fmt(row.pnl)}</b>
              </div>
            )) : <div className={styles.breakdownEmpty}>No settled bets yet.</div>}
          </div>
          <div className={styles.breakdownCard}>
            <div className={styles.breakdownTitle}>P/L by Odds Band</div>
            {premiumInsights.oddsBandBreakdown.length ? premiumInsights.oddsBandBreakdown.map((row) => (
              <div key={`o-${row.name}`} className={styles.breakdownRow}>
                <span>{row.name}</span>
                <b className={row.pnl >= 0 ? styles.good : styles.bad}>£{fmt(row.pnl)}</b>
              </div>
            )) : <div className={styles.breakdownEmpty}>No settled bets yet.</div>}
          </div>
        </div>
      )}

      {/* 🧰 Toolbar */}
      <div className={styles.toolbar}>
        <button onClick={exportCSV}>Export CSV</button>
        <button onClick={loadBets}>↻ Refresh</button>
        <span className={styles.sep} />
        <button
          className={styles.btnSoft}
          onClick={() =>
            cleanup(
              "delete_zero_pending",
              "Delete ALL pending bets with £0 stake from your account?"
            )
          }
          title="Remove test/fake rows with stake £0 and Pending"
        >
          🧽 Clean £0 Pending
        </button>
        <button
          className={styles.btnWarn}
          onClick={() =>
            cleanup(
              "delete_pending",
              "Delete ALL pending bets from your account?"
            )
          }
        >
          ❌ Delete Pending
        </button>
        <button
          className={styles.btnDanger}
          onClick={() =>
            cleanup(
              "delete_all",
              "⚠️ Delete ALL bets in your tracker (cannot be undone)?"
            )
          }
        >
          💣 Nuke All
        </button>
      </div>

      {/* Add new bet */}
      <form onSubmit={addBet} className={styles.form}>
        <select
          name="bet_type"
          value={form.bet_type}
          onChange={handleChange}
        >
          <option value="single">Single</option>
          <option value="acca">Acca</option>
        </select>
        {form.bet_type === "single" && (
          <input
            name="fixture_id"
            value={form.fixture_id}
            onChange={handleChange}
            placeholder="Fixture ID (optional)"
          />
        )}
        <input
          name="teams"
          value={form.teams}
          onChange={handleChange}
          placeholder={form.bet_type === "acca" ? "Acca teams summary (optional)" : "Teams (required if no fixture)"}
        />
        <input
          name="market"
          value={form.market}
          onChange={handleChange}
          placeholder={form.bet_type === "acca" ? "Market (optional, default: X Leg Acca)" : "Market"}
          required={form.bet_type !== "acca"}
        />
        <input
          name="bookmaker"
          value={form.bookmaker}
          onChange={handleChange}
          placeholder="Bookmaker"
        />
        <input
          name="price"
          value={form.price}
          onChange={handleChange}
          placeholder={form.bet_type === "acca" ? "Combined odds (auto)" : "Odds (e.g. 1.91, 3/1, evens)"}
          required={form.bet_type !== "acca"}
          disabled={form.bet_type === "acca"}
        />
        <input
          name="stake"
          value={form.stake}
          onChange={handleChange}
          placeholder="Stake"
          required
        />
        <input
          name="notes"
          value={form.notes}
          onChange={handleChange}
          placeholder="Notes (optional)"
        />
        <button disabled={saving}>{saving ? "Saving…" : "Add Bet"}</button>
      </form>
      {form.bet_type === "acca" && (
        <div className={styles.accaBuilder}>
          <div className={styles.accaHeader}>
            <b>Acca legs</b>
            <span>{validAccaLegs.length} valid legs</span>
            <span>Combined odds: {accaCombinedPrice ? fmt(accaCombinedPrice, 3) : "—"}</span>
          </div>
          {(form.acca_legs || []).map((leg, idx) => (
            <div className={styles.accaLegRow} key={`new-leg-${idx}`}>
              <input
                value={leg.fixture_id}
                onChange={(e) => updateAccaLeg(idx, "fixture_id", e.target.value)}
                placeholder="Fixture ID (optional)"
              />
              <input
                value={leg.teams}
                onChange={(e) => updateAccaLeg(idx, "teams", e.target.value)}
                placeholder="Teams (required if no fixture)"
              />
              <input
                value={leg.market}
                onChange={(e) => updateAccaLeg(idx, "market", e.target.value)}
                placeholder="Market"
              />
              <input
                value={leg.bookmaker}
                onChange={(e) => updateAccaLeg(idx, "bookmaker", e.target.value)}
                placeholder="Bookmaker (optional)"
              />
              <input
                value={leg.price}
                onChange={(e) => updateAccaLeg(idx, "price", e.target.value)}
                placeholder="Odds (e.g. 2.5 or 3/1)"
              />
              <button
                type="button"
                className={styles.btnWarn}
                onClick={() => removeAccaLeg(idx)}
                disabled={(form.acca_legs || []).length <= 2}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className={styles.btnSoft} onClick={addAccaLeg}>
            + Add leg
          </button>
        </div>
      )}

      {/* Filter */}
      <div className={styles.filter}>
        <label>Status:</label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="won">Won</option>
          <option value="lost">Lost</option>
          <option value="void">Void</option>
        </select>
      </div>

      {/* API error (non-blocking) */}
      {apiError && (
        <p style={{ color: "#c00", marginTop: 8 }}>{apiError}</p>
      )}

      {/* Table */}
      {loading ? (
        <p>Loading bets…</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Teams</th>
              <th>Market</th>
              <th>Bookmaker</th>
              <th>Odds</th>
              <th>Stake</th>
              <th>Result</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {premiumFiltered.map((b) => {
              const acca = isAccaBet(b);
              const expanded = !!expandedAccas[b.id];
              const editing = editingBetId === b.id;
              const legs = Array.isArray(b.acca_legs) ? b.acca_legs : [];

              return (
                <React.Fragment key={b.id}>
                  <tr>
                    <td>
                      {buildDisplayTeams(b)}{" "}
                      {acca ? <span className={styles.accaBadge}>Acca</span> : null}
                    </td>
                    <td>{b.market || "—"}</td>
                    <td>{b.bookmaker || "—"}</td>
                    <td>{fmt(b.price)}</td>
                    <td>£{fmt(b.stake)}</td>
                    <td
                      className={
                        b.result
                          ? styles[`r_${(b.result || "").toLowerCase()}`]
                          : styles.r_pending
                      }
                    >
                      {b.result || "Pending"}
                    </td>
                    <td>
                      {acca ? (
                        <button onClick={() => toggleAccaRow(b.id)}>
                          {expanded ? "Hide legs" : "Show legs"}
                        </button>
                      ) : null}
                      <button onClick={() => startEditBet(b)}>Edit</button>
                      <button onClick={() => settleBet(b.id, "won")}>
                        Won
                      </button>
                      <button onClick={() => settleBet(b.id, "lost")}>
                        Lost
                      </button>
                      <button onClick={() => settleBet(b.id, "void")}>
                        Void
                      </button>
                      <button onClick={() => deleteBet(b.id)}>🗑</button>
                    </td>
                  </tr>
                  {expanded && acca && (
                    <tr>
                      <td colSpan="7" className={styles.accaCell}>
                        <div className={styles.accaLegList}>
                          {(legs.length ? legs : []).map((leg, idx) => (
                            <div key={`${b.id}-leg-${idx}`} className={styles.accaLegItem}>
                              <span>{idx + 1}.</span>
                              <span>{toKey(leg.teams) || "—"}</span>
                              <span>{toKey(leg.market) || "—"}</span>
                              <span>{toKey(leg.bookmaker) || "—"}</span>
                              <span>{fmt(leg.price)}</span>
                            </div>
                          ))}
                          {!legs.length && <div>No legs available.</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                  {editing && editDraft && (
                    <tr>
                      <td colSpan="7" className={styles.accaCell}>
                        <div className={styles.editPanel}>
                          <div className={styles.editRow}>
                            <select
                              value={editDraft.bet_type}
                              onChange={(e) => updateEditDraft("bet_type", e.target.value)}
                            >
                              <option value="single">Single</option>
                              <option value="acca">Acca</option>
                            </select>
                            <input
                              value={editDraft.teams}
                              onChange={(e) => updateEditDraft("teams", e.target.value)}
                              placeholder="Teams"
                            />
                            <input
                              value={editDraft.market}
                              onChange={(e) => updateEditDraft("market", e.target.value)}
                              placeholder="Market"
                            />
                            <input
                              value={editDraft.bookmaker}
                              onChange={(e) => updateEditDraft("bookmaker", e.target.value)}
                              placeholder="Bookmaker"
                            />
                            <input
                              value={editDraft.bet_type === "acca" ? fmt(combinedOdds((editDraft.acca_legs || []).filter(isValidLeg)), 3) : editDraft.price}
                              onChange={(e) => updateEditDraft("price", e.target.value)}
                              placeholder="Odds (e.g. 1.91, 3/1, evens)"
                              disabled={editDraft.bet_type === "acca"}
                            />
                            <input
                              value={editDraft.stake}
                              onChange={(e) => updateEditDraft("stake", e.target.value)}
                              placeholder="Stake"
                            />
                            <select
                              value={editDraft.result || ""}
                              onChange={(e) => updateEditDraft("result", e.target.value)}
                            >
                              <option value="">Pending</option>
                              <option value="won">Won</option>
                              <option value="lost">Lost</option>
                              <option value="void">Void</option>
                            </select>
                          </div>
                          {editDraft.bet_type === "acca" && (
                            <div className={styles.accaLegList}>
                              {(editDraft.acca_legs || []).map((leg, idx) => (
                                <div key={`edit-leg-${idx}`} className={styles.accaLegRow}>
                                  <input
                                    value={leg.fixture_id}
                                    onChange={(e) => updateEditLeg(idx, "fixture_id", e.target.value)}
                                    placeholder="Fixture ID (optional)"
                                  />
                                  <input
                                    value={leg.teams}
                                    onChange={(e) => updateEditLeg(idx, "teams", e.target.value)}
                                    placeholder="Teams"
                                  />
                                  <input
                                    value={leg.market}
                                    onChange={(e) => updateEditLeg(idx, "market", e.target.value)}
                                    placeholder="Market"
                                  />
                                  <input
                                    value={leg.bookmaker}
                                    onChange={(e) => updateEditLeg(idx, "bookmaker", e.target.value)}
                                    placeholder="Bookmaker"
                                  />
                                <input
                                  value={leg.price}
                                  onChange={(e) => updateEditLeg(idx, "price", e.target.value)}
                                  placeholder="Odds (e.g. 2.5 or 3/1)"
                                />
                                  <button
                                    type="button"
                                    className={styles.btnWarn}
                                    onClick={() => removeEditLeg(idx)}
                                    disabled={(editDraft.acca_legs || []).length <= 2}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button type="button" className={styles.btnSoft} onClick={addEditLeg}>
                                + Add leg
                              </button>
                            </div>
                          )}
                          <div className={styles.editActions}>
                            <button onClick={() => saveEditBet(b)}>Save</button>
                            <button className={styles.btnSoft} onClick={cancelEditBet}>Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!premiumFiltered.length && (
              <tr>
                <td colSpan="7" style={{ opacity: 0.6 }}>
                  No bets found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
